import { Box } from "@mui/material";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { addVendorValidationRules } from "../../pages/vendor/validation";
import { PurchaseService } from "../../services/purchase.service";
import { ActionButton, FormDialog, InputField, PhoneInput, ToggleButton } from "../v3";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const AddEditVendor = ({ open, onClose, vendorId, onSuccess }) => {
  const [showGst, setShowGst] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [vendorDetails, setVendorDetails] = useState({
    name: undefined,
    primaryPhone: undefined,
    primaryAddress: {
      locality: undefined,
    },
    taxDetails: {
      gstNumber: undefined,
    },
    oldBalance: undefined,
  });

  const isValid = isPhoneValid(vendorDetails.primaryPhone);
  const showError = touched && !isValid;

  useEffect(() => {
    if (open && vendorId) {
      fetchData(vendorId);
    } else if (open && !vendorId) {
      handleClearForm();
    }
  }, [open, vendorId]);

  const fetchData = async (id) => {
    try {
      const { data: vendor } = await PurchaseService.getVendorById(id);
      setVendorDetails({
        name: vendor?.name || undefined,
        primaryPhone: vendor.primaryPhone || undefined,
        primaryAddress: {
          locality: vendor?.primaryAddress?.locality || undefined,
        },
        taxDetails: {
          gstNumber: vendor?.taxDetails?.gstNumber || undefined,
        },
      });
      setShowGst(!!vendor?.taxDetails?.gstNumber);
    } catch (error) {
      toast.error("Failed to fetch vendor details");
      console.error("Error fetching vendor data:", error);
    }
  };

  const handleClearForm = () => {
    setVendorDetails({
      name: undefined,
      primaryPhone: undefined,
      primaryAddress: {
        locality: undefined,
      },
      taxDetails: {
        gstNumber: undefined,
      },
      oldBalance: undefined,
    });
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    if (["locality"].includes(name)) {
      setVendorDetails((prev) => ({
        ...prev,
        primaryAddress: { ...prev.primaryAddress, [name]: value },
      }));
    } else if (name.startsWith("taxDetails.")) {
      const taxDetailsName = name.split(".")[1];
      setVendorDetails((prev) => ({
        ...prev,
        taxDetails: { ...prev.taxDetails, [taxDetailsName]: value },
      }));
    } else {
      setVendorDetails((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "oldBalance") {
      const num = parseFloat(value);
      const decimalRegex = /^\d+(\.\d{1,2})?$/;

      if (value !== "" && (isNaN(num) || num <= 0)) {
        setErrors((prev) => ({ ...prev, oldBalance: "Old balance must be greater than 0" }));
      } else if (value !== "" && !decimalRegex.test(value)) {
        setErrors((prev) => ({ ...prev, oldBalance: "At most 2 decimal places are allowed" }));
      } else {
        setErrors((prev) => {
          const { oldBalance, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleCloseModal = () => {
    handleClearForm();
    setShowGst(false);
    setErrors({});
    onClose();
  };

  const formatVendorPayload = (details, isEdit) => {
    const vendorData = { ...details };

    Object.keys(vendorData).forEach((key) => {
      if (typeof vendorData[key] === "string") {
        vendorData[key] = vendorData[key].trim();
        if (vendorData[key] === "") {
          if (isEdit) vendorData[key] = null;
          else delete vendorData[key];
        }
      }
    });

    if (vendorData.primaryAddress) {
      const locality = vendorData.primaryAddress.locality?.trim();

      if (!locality) {
        if (isEdit) vendorData.primaryAddress = null;
        else delete vendorData.primaryAddress;
      } else {
        vendorData.primaryAddress = {
          locality,
          state: vendorData.primaryAddress.state || "",
          country: vendorData.primaryAddress.country || "",
          pinCode: vendorData.primaryAddress.pinCode || "",
        };
      }
    }

    if (vendorData.taxDetails?.gstNumber !== undefined) {
      const gstNumber = vendorData.taxDetails.gstNumber?.trim();

      if (!gstNumber) {
        if (isEdit) vendorData.taxDetails = null;
        else delete vendorData.taxDetails;
      } else vendorData.taxDetails.gstNumber = gstNumber;
    }

    return vendorData;
  };

  const handleSaveVendor = async () => {
    setErrors({});

    const formattedVendorDetails = formatVendorPayload(vendorDetails, !!vendorId);

    const payload = {
      ...formattedVendorDetails,
      ...(formattedVendorDetails.primaryPhone !== null ? { primaryPhone: formattedVendorDetails.primaryPhone } : {}),
      ...(formattedVendorDetails.oldBalance !== null ? { oldBalance: Number(formattedVendorDetails.oldBalance) } : {}),
      ...(formattedVendorDetails.taxDetails ? { taxDetails: formattedVendorDetails.taxDetails } : {}),
      ...(formattedVendorDetails.primaryAddress ? { primaryAddress: formattedVendorDetails.primaryAddress } : {}),
    };

    if (vendorId) delete payload.oldBalance;

    const errors = addVendorValidationRules(payload);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setLoading(true);
    try {
      if (vendorId) {
        await PurchaseService.updateVendor(vendorId, payload);
      } else {
        await PurchaseService.createVendor(payload);
      }

      toast.success(`Vendor ${vendorId ? "updated" : "added"} successfully`);
      if (onSuccess) onSuccess();
      handleCloseModal();
    } catch (error) {
      toast.error(`Failed to ${vendorId ? "update" : "add"} vendor`);
      console.error("🚀 ~ handleSaveVendor ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={vendorId ? "Edit Vendor" : "Add Vendor"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveVendor} loading={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Vendor Name"
            name="name"
            value={vendorDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <InputField
            label="place"
            name="locality"
            value={vendorDetails.primaryAddress.locality}
            onChange={updateDetailsState}
            fullWidth
            autoComplete="on"
          />
        </Box>
        <Box display="flex" gap={2}>
          <PhoneInput
            label="Mobile Number"
            defaultCountry="in"
            value={vendorDetails.primaryPhone}
            size="small"
            fullWidth
            onChange={(e164) => setVendorDetails((prev) => ({ ...prev, primaryPhone: e164 }))}
            onBlur={() => setTouched(true)}
            error={showError}
            helperText={showError ? "Enter a valid phone number" : " "}
          />
        </Box>
        {!vendorId && (
          <Box display="flex" gap={2}>
            <InputField
              label="Old Balance"
              name="oldBalance"
              type="number"
              value={vendorDetails.oldBalance}
              onChange={updateDetailsState}
              error={!!errors.oldBalance}
              helperText={errors.oldBalance}
              fullWidth
            />
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={2} ml={1}>
          <ToggleButton label="GST Number" checked={showGst} onToggleChange={() => setShowGst((prev) => !prev)} />
        </Box>

        {showGst && (
          <Box display="flex" gap={2} mt={2}>
            <InputField
              label="GST Number"
              name="taxDetails.gstNumber"
              value={vendorDetails?.taxDetails?.gstNumber}
              onChange={updateDetailsState}
              error={!!errors?.taxDetails?.gstNumber}
              helperText={errors?.taxDetails?.gstNumber}
              fullWidth
            />
          </Box>
        )}
      </Box>
    </FormDialog>
  );
};
